import {HubConnection, HubConnectionBuilder, LogLevel} from '@microsoft/signalr'
import {useEffect, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {useAuth} from 'react-oidc-context'
import {AUTH_TOKEN_KEY, env} from '../../../../data/constants'
import {CorrespondenceApiSlice} from '../CorrespondenceApiSlice'

interface ICorrespondenceUnreadCountUpdatedEvent {
  total: number
}

interface ICorrespondenceThreadUpdatedEvent {
  threadId: string
}

const UNREAD_COUNT_UPDATED_METHOD = 'CorrespondenceUnreadCountUpdated'
const THREAD_UPDATED_METHOD = 'CorrespondenceThreadUpdated'

export function useCorrespondenceRealtime() {
  const auth = useAuth()
  const dispatch = useDispatch()
  const connectionRef = useRef<HubConnection | null>(null)
  const accessToken = auth.user?.access_token ?? localStorage.getItem(AUTH_TOKEN_KEY) ?? ''

  useEffect(() => {
    if (!auth.isAuthenticated || !env.CorrespondenceHub || !accessToken) {
      void connectionRef.current?.stop()
      connectionRef.current = null
      return
    }

    let retryTimeout: ReturnType<typeof setTimeout> | null = null

    const connection = new HubConnectionBuilder()
      .withUrl(env.CorrespondenceHub, {
        accessTokenFactory: () => auth.user?.access_token ?? localStorage.getItem(AUTH_TOKEN_KEY) ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build()

    connection.on(
      UNREAD_COUNT_UPDATED_METHOD,
      (payload: ICorrespondenceUnreadCountUpdatedEvent) => {
        dispatch(
          CorrespondenceApiSlice.util.upsertQueryData('getUnreadCount', undefined, {
            total: payload.total,
          })
        )
      }
    )

    connection.on(THREAD_UPDATED_METHOD, (payload: ICorrespondenceThreadUpdatedEvent) => {
      dispatch(
        CorrespondenceApiSlice.util.invalidateTags([
          {type: 'CorrespondenceThreads', id: 'LIST'},
          {type: 'CorrespondenceThreads', id: payload.threadId},
        ])
      )
    })

    connection.onreconnected(() => {
      dispatch(CorrespondenceApiSlice.util.resetApiState())
    })

    connection.onclose(() => {
      connectionRef.current = null
    })

    connectionRef.current = connection

    const startConnection = async () => {
      try {
        await connection.start()
      } catch (error) {
        console.error('Failed to start correspondence SignalR connection', error)

        if (!retryTimeout) {
          retryTimeout = setTimeout(() => {
            retryTimeout = null
            void startConnection()
          }, 3000)
        }
      }
    }

    void startConnection()

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }

      connection.off(UNREAD_COUNT_UPDATED_METHOD)
      connection.off(THREAD_UPDATED_METHOD)
      void connection.stop()
      if (connectionRef.current === connection) {
        connectionRef.current = null
      }
    }
  }, [accessToken, auth.isAuthenticated, auth.user?.access_token, dispatch])
}
